<template>
	<section ref="elementRef" :class="containerClass">
		<div class="container px-4 mx-auto">
			<div class="max-w-4xl mx-auto">
				<!-- Main CTA Section -->
				<div class="mb-8 text-center">
					<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
						{{ headline }}
					</h2>
					<p v-if="subheadline" class="mb-8 text-xl text-gray-600">
						{{ subheadline }}
					</p>
				</div>

				<!-- Primary CTA Button -->
				<div class="flex flex-col justify-center gap-4 mb-8 sm:flex-row">
					<button :class="primaryButtonClass" @click="handlePrimaryCtaClick">
						<SvgIcon v-if="showIcon" name="arrowRight" size="sm" class="mr-2" />
						{{ ctaText }}
					</button>

					<!-- Secondary CTA -->
					<button
						v-if="secondaryCtaText"
						class="px-8 py-4 font-semibold text-gray-900 transition-colors border-2 border-gray-300 rounded-xl hover:border-gray-400"
						@click="handleSecondaryCtaClick"
					>
						{{ secondaryCtaText }}
					</button>
				</div>

				<!-- Trust Indicators -->
				<div v-if="trustIndicators && trustIndicators.length > 0" class="mb-8">
					<div class="flex flex-wrap items-center justify-center gap-6">
						<div
							v-for="indicator in trustIndicators"
							:key="indicator"
							class="flex items-center text-sm text-gray-600"
						>
							<SvgIcon name="checkCircle" size="sm" color="success" class="mr-2" />
							{{ indicator }}
						</div>
					</div>
				</div>

				<!-- Urgency Banner -->
				<div
					v-if="showUrgency && urgencyMessage"
					class="p-4 mt-8 border border-orange-200 rounded-lg bg-orange-50"
				>
					<div class="flex items-center justify-center">
						<SvgIcon name="zap" size="md" color="warning" class="mr-2" />
						<span class="font-semibold text-orange-900">{{ urgencyMessage }}</span>
					</div>
				</div>

				<!-- Social Proof Stats -->
				<div
					v-if="showStats && stats && stats.length > 0"
					class="grid grid-cols-1 gap-6 mt-12 md:grid-cols-3"
				>
					<div
						v-for="stat in stats"
						:key="stat.label"
						class="p-6 text-center rounded-lg bg-gray-50"
					>
						<div class="mb-2 text-3xl font-bold text-blue-600">
							{{ stat.value }}
						</div>
						<div class="text-gray-600">
							{{ stat.label }}
						</div>
					</div>
				</div>

				<!-- Risk Reversal -->
				<div v-if="showRiskReversal" class="mt-8 text-sm text-center text-gray-600">
					<p>{{ riskReversalText }}</p>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useComponentTracking } from '@/composables/useComponentTracking';
import SvgIcon from './icons/SvgIcon.vue';

interface Stat {
	value: string;
	label: string;
}

interface Props {
	headline: string;
	subheadline?: string;
	ctaText: string;
	secondaryCtaText?: string;
	variant?: 'gradient' | 'solid' | 'outline';
	backgroundColor?: string;
	trustIndicators?: string[];
	showUrgency?: boolean;
	urgencyMessage?: string;
	showStats?: boolean;
	stats?: Stat[];
	showRiskReversal?: boolean;
	riskReversalText?: string;
	showIcon?: boolean;
	ctaLocation?: string;
	pageSection?: string;
}

const props = withDefaults(defineProps<Props>(), {
	variant: 'gradient',
	backgroundColor: 'bg-white',
	showUrgency: false,
	showStats: false,
	showRiskReversal: false,
	riskReversalText: '100% Geld-zurück-Garantie. Keine Fragen gestellt.',
	showIcon: true,
	ctaLocation: 'conversion_section',
	pageSection: 'conversion',
});

const emit = defineEmits<{
	'cta-click': [];
	'secondary-cta-click': [];
}>();

// Initialize tracking with visibility-based view tracking
// Component view is automatically tracked when element becomes visible
const { elementRef, trackCtaClick } = useComponentTracking({
	componentName: 'ConversionElements',
	sectionId: props.pageSection || 'conversion',
	sectionType: 'conversion',
	trackOnVisible: true, // Track only when visible (default)
});

/**
 * Handle primary CTA click with tracking
 */
function handlePrimaryCtaClick(): void {
	trackCtaClick(props.ctaText, {
		variant: props.variant,
		cta_location: props.ctaLocation,
		has_urgency: props.showUrgency,
		has_stats: props.showStats,
	});
	emit('cta-click');
}

/**
 * Handle secondary CTA click with tracking
 */
function handleSecondaryCtaClick(): void {
	trackCtaClick(props.secondaryCtaText || 'Secondary CTA', {
		variant: props.variant,
		cta_location: props.ctaLocation,
		cta_type: 'secondary',
	});
	emit('secondary-cta-click');
}

const containerClass = computed(() => `py-16 ${props.backgroundColor}`);

const primaryButtonClass = computed(() => {
	const baseClass =
		'inline-flex items-center px-8 py-4 font-semibold rounded-xl transition-colors shadow-lg';
	const variants: Record<string, string> = {
		gradient:
			'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800',
		solid: 'bg-blue-600 text-white hover:bg-blue-700',
		outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50',
	};
	return `${baseClass} ${variants[props.variant] || variants.gradient}`;
});
</script>
